import React, { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { MapPin, Check, CalendarDays, ArrowUpRight, Star } from "lucide-react";
import Reveal from "../components/site/Reveal";
import { QUARTIERS, quartierPath } from "../seoPages";
import Typewriter from "../components/site/Typewriter";
import EstimateBar from "../components/site/EstimateBar";
import { CtaLink, SectionTitle, LogoTitle } from "../components/site/Common";
import { OrganicDot } from "../components/site/Logo";
import { Services, Testimonials, FinalCta, PlatformsBand } from "../components/site/HomeSections";
import { CityGuide, CityFaq, CityWhy, CityLocal } from "../components/site/CitySeoBlocks";
import { useQuery } from "@tanstack/react-query";
import { getCityPage, slugify, estimate, aCity } from "../mock";
import { nearestCities } from "../seo";
import { ALIASES, cityForAlias } from "../cities";
import { CityServices } from "../components/site/ServiceBlocks";
import { api } from "../lib/api";
import { LocalAdvisor } from "../components/site/LocalAdvisor";
import { OPTION_PAGES, OPTION_CITIES, optionCityPath } from "../options";

export default function VillePage() {
  const { slug } = useParams();
  const city = useMemo(() => getCityPage(slug), [slug]);
  const { data: ai } = useQuery({ queryKey: ["city-content", slug], queryFn: () => api.cityContent(slug), enabled: !!city, retry: false, staleTime: 300_000 });
  if (!city) {
    const alias = cityForAlias(slug);
    return <Navigate to={alias ? `/villes/${slugify(alias)}` : "/villes"} replace />;
  }
  const sim = estimate({ city: city.name, type: "appartement", bedrooms: 1, guests: 2 });
  const others = nearestCities(city.name, 9);

  return (
    <div data-testid="ville-page" key={slug}>
      <section className="relative overflow-hidden bg-gradient-to-b from-[#f3ede1] to-[#faf7f0]">
        <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-[#d9895a]/15 blur-3xl" />
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-12 lg:pt-16 pb-20 grid lg:grid-cols-12 gap-12 items-center relative">
          <div className="lg:col-span-6">
            <Reveal>
              <nav className="text-sm font-semibold text-[#6d7268]" data-testid="breadcrumb">
                <Link to="/" className="hover:text-[#13261c]">Accueil</Link> <span className="mx-1.5">/</span>
                <Link to="/villes" className="hover:text-[#13261c]">Villes</Link> <span className="mx-1.5">/</span>
                <span className="text-[#13261c]">{city.name}</span>
              </nav>
            </Reveal>
            <Reveal delay={60}>
              <span className="mt-6 inline-flex items-center gap-2 rounded-full bg-white ring-1 ring-[#e6dcc8] px-4 py-2 text-sm font-semibold text-[#1c3426] shadow-sm">
                <span className="flex">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-4 h-4 fill-[#d9895a] text-[#d9895a]" />)}</span>
                4,9/5 par +1 200 voyageurs en Superhôte Airbnb
              </span>
            </Reveal>
            <Reveal delay={120}>
              <h1 className="mt-4 text-[#13261c] leading-[1.05]">
                <span data-testid="city-title" className={`block font-logo font-black tracking-[-0.03em] break-words ${city.name.length > 16 ? "text-[38px] sm:text-[50px] lg:text-[56px]" : "text-[48px] sm:text-[64px] lg:text-[76px]"}`} style={{ lineHeight: 0.95 }}>{city.name}<OrganicDot className="inline-block align-baseline ml-1 w-3 h-3 lg:w-4 lg:h-4" /></span>
                {ALIASES[city.name] && <span className="block mt-3 text-base font-sans font-semibold text-[#6d7268]" data-testid="city-aliases">{city.name === "Ploubalay" ? "Commune de " : city.name === "Saint-Malo" ? "" : "Avec "}{(city.name === "Saint-Malo" ? ["Intra-Muros", ...ALIASES[city.name]] : ALIASES[city.name]).map((n, i) => {
                  const q = QUARTIERS.find((x) => x.city === city.name && x.name === n);
                  return <React.Fragment key={n}>{i ? ", " : ""}{q ? <Link to={quartierPath(q)} className="underline decoration-[#d9895a] underline-offset-4 hover:text-[#13261c]" data-testid={`city-alias-link-${q.slug}`}>{n}</Link> : n}</React.Fragment>;
                })}</span>}
                <span className="block font-display font-medium text-[22px] sm:text-[26px] lg:text-[30px] leading-snug mt-5 text-[#2a4636]">Votre conciergerie {aCity(city.name)} pour</span>
                <span className="block font-display font-bold text-[26px] sm:text-[32px] lg:text-[38px] text-[#c4703f] leading-[1.2] mt-1 [overflow-wrap:anywhere]">
                  <Typewriter words={city.words} />
                </span>
              </h1>
            </Reveal>
            <Reveal delay={180}>
              <p className="mt-5 text-lg text-[#4a5249] leading-relaxed max-w-xl" data-testid="city-intro">{city.text}</p>
            </Reveal>
            <Reveal delay={240} className="mt-8 flex flex-wrap gap-3">
              <CtaLink to="/rendez-vous" testid="city-rdv">Prendre rendez-vous</CtaLink>
              <CtaLink to={`/proprietaires?city=${encodeURIComponent(city.name)}`} variant="outline" testid="city-sim">Simuler mon potentiel</CtaLink>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={150} className="relative">
              <div className="relative rounded-t-[220px] rounded-b-[36px] overflow-hidden aspect-[4/5] max-w-[480px] mx-auto shadow-[0_40px_80px_-30px_rgba(19,38,28,0.5)]">
                <img fetchPriority="high" src={city.img} alt={`Location saisonnière ${aCity(city.name)}`} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#13261c]/60 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 text-white">
                  <p className="text-sm font-semibold opacity-90 flex items-center gap-1.5"><MapPin className="w-4 h-4" />{city.area}</p>
                  <p className="font-logo font-black tracking-[-0.025em] text-3xl">{city.name}<OrganicDot className="inline-block w-2 h-2 ml-0.5 align-baseline" /></p>
                </div>
              </div>
              {city.photo && (
                <p className="mt-3 text-center text-[11px] text-[#6d7268]" data-testid="photo-credit">
                  Photo : {city.photo.page ? <><a href={city.photo.page} target="_blank" rel="noopener noreferrer" className="underline hover:text-[#13261c]">{city.photo.author || "Wikimedia Commons"}</a>{city.photo.license ? ` · ${city.photo.license}` : ""} · Wikimedia Commons</> : city.photo.author}
                </p>
              )}
              <div className="floaty absolute left-0 sm:left-4 top-10 bg-white rounded-3xl p-4 pr-6 shadow-[0_25px_50px_-20px_rgba(19,38,28,0.45)]">
                <p className="text-xs font-semibold text-[#6d7268]">Potentiel indicatif · T2</p>
                <p className="text-xl font-extrabold text-[#13261c]">≈ {sim.monthly.toLocaleString("fr-FR")} € / mois</p>
              </div>
              <div className="floaty absolute right-0 sm:right-4 bottom-20 bg-white rounded-3xl p-4 shadow-[0_25px_50px_-20px_rgba(19,38,28,0.45)]" style={{ animationDelay: "1.5s" }}>
                <div className="flex">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-4 h-4 fill-[#d9895a] text-[#d9895a]" />)}</div>
                <p className="text-sm font-bold text-[#13261c] mt-1">{city.name}, {city.tagline}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <PlatformsBand />

      <LocalAdvisor city={city.name} />
      {OPTION_CITIES.includes(city.name) && (
        <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-10" data-testid="city-options">
          <p className="text-[#4a5249]">Options en supplément {aCity(city.name)} : {OPTION_PAGES.map((o, i) => <React.Fragment key={o.slug}>{i ? " · " : ""}<Link to={optionCityPath(o, city.name)} data-testid={`city-option-${o.slug}`} className="font-bold text-[#13261c] underline decoration-[#d9895a] underline-offset-4 hover:text-[#c4703f]">{o.name} {city.name}</Link></React.Fragment>)}</p>
        </section>
      )}

      <section className="py-20" data-testid="city-spots">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <SectionTitle kicker="On connaît le terrain" title="on intervient partout" script={aCity(city.name)} text="Notre équipe vit sur le secteur : accueil des voyageurs, ménage et petites interventions en un temps record." />
            <Reveal delay={100} className="mt-8 flex flex-wrap gap-2">
              {city.spots.map((s) => (
                <span key={s} className="inline-flex items-center gap-2 rounded-full bg-white ring-1 ring-[#e6dcc8] px-4 py-2.5 font-semibold text-[#13261c] hover:bg-[#13261c] hover:text-white transition-colors"><MapPin className="w-4 h-4 text-[#c4703f]" />{s}</span>
              ))}
            </Reveal>
          </div>
          <Reveal delay={120} className="rounded-[32px] bg-[#13261c] text-[#faf7f0] p-8 lg:p-10 grain relative overflow-hidden">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#d9895a]">{aCity(city.name).replace(/^à/, "À").replace(/^au/, "Au")}, on s'occupe de</p>
            <ul className="mt-6 space-y-4">
              {["Création et diffusion de votre annonce", "Accueil et remise des clés 7j/7", "Ménage et linge fourni", "Prix ajustés selon la saison et les événements locaux", "Astreinte voyageurs 7j/7 jusqu'à 23h"].map((t) => (
                <li key={t} className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#d9895a] grid place-items-center shrink-0"><Check className="w-3.5 h-3.5 text-[#13261c]" strokeWidth={3} /></span>{t}</li>
              ))}
            </ul>
            <Link to="/rendez-vous" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#d9895a] hover:bg-[#e39d72] text-[#13261c] px-6 py-3.5 font-bold transition-colors"><CalendarDays className="w-4 h-4" />Rencontrer l'équipe</Link>
          </Reveal>
        </div>
      </section>

      <CityGuide city={city} ai={ai} />

      <CityLocal city={city} />

      <CityWhy city={city} />

      <CityServices city={city} />

      <section className="pb-20">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <Reveal className="mb-8"><LogoTitle as="h2" size="md" center title="combien ça rapporte ?" script={`votre logement ${aCity(city.name)}`} /></Reveal>
          <Reveal delay={80}><EstimateBar defaultCity={city.name} /></Reveal>
        </div>
      </section>

      <Services />

      {others.length > 0 && (
        <section className="py-20" data-testid="city-others">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <h2 className="font-logo font-black tracking-[-0.025em] text-3xl lg:text-4xl text-[#13261c]">communes voisines de {city.name}<OrganicDot className="inline-block w-2.5 h-2.5 ml-1 align-baseline" /></h2>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {others.map((c) => (
                <Link key={c.name} to={`/villes/${slugify(c.name)}`} data-testid={`city-neighbour-${slugify(c.name)}`} className="group flex items-center justify-between rounded-2xl p-5 ring-1 ring-[#e6dcc8] bg-white hover:bg-[#13261c] hover:text-white text-[#13261c] transition-colors">
                  <span className="flex items-center gap-3 font-bold"><MapPin className="w-5 h-5 text-[#c4703f] group-hover:text-[#d9895a]" />Conciergerie {c.name}</span>
                  <span className="flex items-center gap-2 shrink-0 whitespace-nowrap text-sm text-[#c4703f] group-hover:text-[#d9895a]">{c.km} km<ArrowUpRight className="w-5 h-5 opacity-40 group-hover:opacity-100 transition-opacity" /></span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Testimonials />
      <CityFaq city={city} ai={ai} />
      <FinalCta />
    </div>
  );
}
